/* eslint-disable camelcase */
import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardActionArea,
  Divider,
  IconButton,
  CircularProgress,
  LinearProgress,
  Badge,
  Chip,
  Alert,
} from "@mui/material"
import {
  Dashboard as DashboardIcon,
  Public,
  Policy,
  Download,
  Refresh,
  HowToVote,
  Assessment,
  WarningAmber,
  CloudOff,
  CloudDone,
} from "@mui/icons-material"
import { BarChart } from "@mui/x-charts/BarChart"
import { PieChart } from "@mui/x-charts/PieChart"
import dayjs from "dayjs"
import localforage from "localforage"

import Header from "../../../../layouts/Header"
import { api } from "../../../../api"

// Tema oscuro/táctico para el War Room (Alto Contraste)
const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#ed6c02" },
    success: { main: "#2E7D32" },
    error: { main: "#d32f2f" },
  },
})

const DashboardCentroComputo = () => {
  const navigate = useNavigate()
  const [isOffline, setIsOffline] = useState(!navigator.onLine)
  const [isLoading, setIsLoading] = useState(false)
  const [errorAcceso, setErrorAcceso] = useState(null)
  const [ultimaActualizacion, setUltimaActualizacion] = useState(dayjs().format("HH:mm:ss"))

  // Estados de RLS
  const [esGlobal, setEsGlobal] = useState(false)
  const [cobertura, setCobertura] = useState("Cargando...")

  // KPIs
  const [kpis, setKpis] = useState({
    actasProcesadas: 0,
    actasEsperadas: 1,
    actasNovedad: 0,
    votosValidos: 0,
    blancos: 0,
    nulos: 0,
  })

  const [resultadosCandidatos, setResultadosCandidatos] = useState([])

  // ========================================================
  // INICIALIZACIÓN Y POLLING AGRESIVO (WAR ROOM)
  // ========================================================
  useEffect(() => {
    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)
    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)

    cargarDatosGlobales()

    // Auto-refresco cada 30 segundos si hay internet
    const interval = setInterval(() => {
      if (navigator.onLine) cargarDatosGlobales(true)
    }, 30000)

    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
      clearInterval(interval)
    }
  }, [])

  const cargarDatosGlobales = async (silencioso = false) => {
    if (!silencioso) setIsLoading(true)
    setErrorAcceso(null)

    try {
      if (isOffline) throw new Error("Offline")

      const res = await api.post("/ControlElectoral/getResultadosDashboard", {})
      const data = res?.data !== undefined ? res.data : res

      if (data && data.success === false) {
        setErrorAcceso(data.message)
        return
      }

      setKpis(data.kpis)
      setResultadosCandidatos(data.candidatos || [])
      setEsGlobal(data.es_global)
      setCobertura(data.cobertura)
      setUltimaActualizacion(dayjs().format("HH:mm:ss"))

      // Respaldo PWA
      await localforage.setItem("dash_central_cache", data)
    } catch (error) {
      console.log("Cargando tablero central desde caché offline...")
      if (error?.response?.status === 403) {
        setErrorAcceso(error.response.data.message || "Usuario no autorizado.")
      } else {
        const cache = await localforage.getItem("dash_central_cache")
        if (cache) {
          setKpis(cache.kpis)
          setResultadosCandidatos(cache.candidatos || [])
          setEsGlobal(cache.es_global)
          setCobertura(cache.cobertura)
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Rutas de navegación del War Room
  const nav = {
    geografia: "/home/dashboard/ControlElectoral/CenCom/GeografiaElectoral",
    auditoria: "/home/dashboard/ControlElectoral/CenCom/AuditoriaActas",
    reportes: "/home/dashboard/ControlElectoral/CenCom/ReportesLegal",
  }

  const actasValidas = kpis.actasProcesadas - kpis.actasNovedad
  const actasFaltantes = kpis.actasEsperadas > kpis.actasProcesadas ? kpis.actasEsperadas - kpis.actasProcesadas : 0

  // Evitamos dividir por cero en el progreso
  const totalEsperadoSeguro = kpis.actasEsperadas > 0 ? kpis.actasEsperadas : 1
  const avancePorcentaje = Math.round((kpis.actasProcesadas / totalEsperadoSeguro) * 100)

  const chartActas = [
    { id: 0, value: actasValidas, label: "Actas Válidas", color: "#2E7D32" },
    { id: 1, value: kpis.actasNovedad, label: "En Auditoría", color: "#d32f2f" },
    { id: 2, value: actasFaltantes, label: "Por Recibir", color: "#e0e0e0" },
  ]

  return (
    <ThemeProvider theme={theme}>
      <Header />

      <div className="main main-app p-4" style={{ backgroundColor: "#eef2f6", minHeight: "100vh" }}>
        {/* ENCABEZADO WAR ROOM */}
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "flex-end" }}
          mb={3}
          gap={2}
        >
          <Box>
            <Typography variant="h4" fontWeight="900" color="primary.main" display="flex" alignItems="center">
              <DashboardIcon sx={{ fontSize: 40, mr: 1 }} /> CENTRO DE CÓMPUTO MANUEL GILCES - 2027-2031
            </Typography>
            <Typography variant="subtitle1" color="textSecondary" fontWeight="bold">
              Monitor de Escrutinio en Tiempo Real
            </Typography>
          </Box>

          <Box
            display="flex"
            alignItems="center"
            bgcolor="white"
            p={1}
            px={2}
            borderRadius={2}
            boxShadow={1}
            flexWrap="wrap"
            gap={1}
          >
            {isOffline ? <CloudOff color="error" /> : <CloudDone color="success" />}
            {isLoading && <CircularProgress size={20} sx={{ mr: 1, color: "#196C87" }} />}
            <Box textAlign="right" mx={2}>
              <Typography variant="caption" color="textSecondary" display="block" lineHeight={1}>
                ÚLTIMA ACTUALIZACIÓN
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold" color={isOffline ? "error.main" : "success.main"}>
                {ultimaActualizacion}
              </Typography>
            </Box>
            <IconButton
              onClick={() => cargarDatosGlobales(false)}
              disabled={isLoading || isOffline}
              color="primary"
              sx={{ border: "1px solid #196C87" }}
            >
              <Refresh />
            </IconButton>
          </Box>
        </Box>

        {errorAcceso ? (
          <Alert severity="error" variant="filled" sx={{ borderRadius: 2, p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              ACCESO RESTRINGIDO
            </Typography>
            <Typography variant="body1">{errorAcceso}</Typography>
          </Alert>
        ) : (
          <>
            {/* INDICADOR DE COBERTURA RLS */}
            <Box display="flex" alignItems="center" mb={3} p={1.5} bgcolor="#e1f5fe" borderRadius={2}>
              <Typography variant="subtitle2" color="primary.dark" fontWeight="bold" display="flex" alignItems="center">
                📍 Cobertura: {cobertura}
              </Typography>
              {esGlobal && (
                <Chip
                  icon={<Public fontSize="small" />}
                  label="MODO GLOBAL"
                  color="info"
                  size="small"
                  sx={{ ml: 2, fontWeight: "bold" }}
                />
              )}
            </Box>

            {/* ======================================================== */}
            {/* FILA 1: TARJETAS DE NAVEGACIÓN Y ALERTAS                 */}
            {/* ======================================================== */}
            <Grid container spacing={3} mb={4}>
              <Grid item xs={12} md={4}>
                <Card
                  elevation={3}
                  sx={{
                    borderRadius: 3,
                    borderTop: "4px solid #196C87",
                    height: "100%",
                    transition: "0.2s",
                    "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
                  }}
                >
                  <CardActionArea onClick={() => navigate(nav.geografia)} sx={{ p: 3, height: "100%" }}>
                    <Box display="flex" alignItems="center">
                      <Public sx={{ fontSize: 50, color: "#196C87", mr: 2 }} />
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          Geografía Electoral
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Análisis de rendimiento por Zonas
                        </Typography>
                      </Box>
                    </Box>
                  </CardActionArea>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Badge badgeContent={kpis.actasNovedad} color="error" sx={{ width: "100%", height: "100%" }}>
                  <Card
                    elevation={3}
                    sx={{
                      borderRadius: 3,
                      borderTop: "4px solid #d32f2f",
                      width: "100%",
                      height: "100%",
                      transition: "0.2s",
                      "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
                    }}
                  >
                    <CardActionArea onClick={() => navigate(nav.auditoria)} sx={{ p: 3, height: "100%" }}>
                      <Box display="flex" alignItems="center">
                        <Policy sx={{ fontSize: 50, color: "#d32f2f", mr: 2 }} />
                        <Box>
                          <Typography variant="h6" fontWeight="bold">
                            Auditoría de Actas
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Corrección de inconsistencias
                          </Typography>
                        </Box>
                      </Box>
                    </CardActionArea>
                  </Card>
                </Badge>
              </Grid>

              {/* El módulo legal suele estar restringido a administradores */}
              <Grid item xs={12} md={4}>
                <Card
                  elevation={3}
                  sx={{
                    borderRadius: 3,
                    borderTop: "4px solid #2E7D32",
                    height: "100%",
                    transition: "0.2s",
                    "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
                  }}
                >
                  <CardActionArea onClick={() => navigate(nav.reportes)} sx={{ p: 3, height: "100%" }}>
                    <Box display="flex" alignItems="center">
                      <Download sx={{ fontSize: 50, color: "#2E7D32", mr: 2 }} />
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          Exportación Legal
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Generación de respaldos PDF
                        </Typography>
                      </Box>
                    </Box>
                  </CardActionArea>
                </Card>
              </Grid>
            </Grid>

            {/* ======================================================== */}
            {/* FILA 2: PANELES DE DATOS (ESCRUTINIO)                    */}
            {/* ======================================================== */}
            <Grid container spacing={3}>
              {/* PANEL IZQUIERDO: RESULTADOS DE CANDIDATOS */}
              <Grid item xs={12} md={8}>
                <Paper elevation={4} sx={{ p: 3, borderRadius: 3, height: "100%" }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <HowToVote color="primary" sx={{ mr: 1, fontSize: 30 }} />
                    <Typography variant="h5" fontWeight="bold" color="textPrimary">
                      Resultados en Tiempo Real
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />

                  <Box sx={{ flexGrow: 1, height: 400 }}>
                    {resultadosCandidatos.length > 0 ? (
                      <BarChart
                        dataset={resultadosCandidatos}
                        yAxis={[{ scaleType: "band", dataKey: "cand_nombre" }]}
                        series={[
                          {
                            dataKey: "votos",
                            valueFormatter: (v) => `${v.toLocaleString()} votos`,
                            // Si la librería soporta pintar por dataKey interno:
                            color: "#196C87",
                          },
                        ]}
                        layout="horizontal"
                        height={400}
                        margin={{ left: 150, right: 30, top: 20, bottom: 20 }}
                      />
                    ) : (
                      <Typography align="center" color="textSecondary" mt={10}>
                        Esperando ingreso de actas...
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* PANEL DERECHO: AVANCE DEL OPERATIVO */}
              <Grid item xs={12} md={4}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Paper elevation={4} sx={{ p: 3, borderRadius: 3 }}>
                      <Typography variant="h6" fontWeight="bold" mb={2} display="flex" alignItems="center">
                        <Assessment color="primary" sx={{ mr: 1 }} /> Avance de Transmisión
                      </Typography>

                      <Box position="relative" display="flex" justifyContent="center" alignItems="center" mb={2}>
                        <PieChart
                          series={[
                            { data: chartActas, innerRadius: 60, outerRadius: 100, paddingAngle: 2, cornerRadius: 5 },
                          ]}
                          width={250}
                          height={250}
                          slotProps={{ legend: { hidden: true } }}
                          margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                        />
                        <Box
                          position="absolute"
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Typography variant="h3" fontWeight="bold" color="primary.main">
                            {avancePorcentaje}%
                          </Typography>
                        </Box>
                      </Box>

                      <Box display="flex" justifyContent="space-between" alignItems="center" px={1}>
                        <Typography variant="body1" fontWeight="bold" color="success.main">
                          {kpis.actasProcesadas} Ingresadas
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" color="textSecondary">
                          {kpis.actasEsperadas} Esperadas
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={avancePorcentaje}
                        sx={{ mt: 1, height: 8, borderRadius: 4 }}
                      />
                    </Paper>
                  </Grid>

                  {/* TARJETAS DE NOVEDADES */}
                  <Grid item xs={12}>
                    <Paper
                      elevation={4}
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        bgcolor: kpis.actasNovedad > 0 ? "#fff3e0" : "#e8f5e9",
                        borderLeft: `6px solid ${kpis.actasNovedad > 0 ? "#ed6c02" : "#2E7D32"}`,
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            color={kpis.actasNovedad > 0 ? "secondary.main" : "success.main"}
                            textTransform="uppercase"
                          >
                            {kpis.actasNovedad > 0 ? "Actas con Novedad" : "Operación Limpia"}
                          </Typography>
                          <Typography variant="h4" fontWeight="bold" color="textPrimary">
                            {kpis.actasNovedad}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {kpis.actasNovedad > 0 ? "Requieren auditoría urgente" : "Todas las actas cuadraron"}
                          </Typography>
                        </Box>
                        <WarningAmber
                          sx={{ fontSize: 50, color: kpis.actasNovedad > 0 ? "#ed6c02" : "#2E7D32", opacity: 0.5 }}
                        />
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
      </div>
    </ThemeProvider>
  )
}

export default DashboardCentroComputo
